// hide_note.js: the mandatory reason Care must give when hiding or showing a teacher to trial
// students (PROD-9278). Mirrors validateHideNote in goserver session_reco/hide_note.go — the server
// is the gate, this only spares Care a round trip, so the two rules must stay identical.

export const HIDE_NOTE_MAX_LEN = 500;

export const HIDE_NOTE_REQUIRED = 'note is required';
export const HIDE_NOTE_TOO_LONG = `note must be ${HIDE_NOTE_MAX_LEN} characters or fewer`;

// Counts code points, not UTF-16 units, to match Go's utf8.RuneCountInString.
const rune_length = (text) => [...text].length;

/**
 * Validates a hide/unhide reason.
 * @param {string} note raw text from the textarea
 * @returns {{ value: string, error: string }} trimmed note, or the error to show (never both)
 */
export const validate_hide_note = (note) => {
  const trimmed = (note || '').trim();

  if (trimmed === '') {
    return { value: '', error: HIDE_NOTE_REQUIRED };
  }
  if (rune_length(trimmed) > HIDE_NOTE_MAX_LEN) {
    return { value: '', error: HIDE_NOTE_TOO_LONG };
  }
  return { value: trimmed, error: '' };
};

/**
 * Reads the message the API sent back, so Care sees why a save was refused rather than a bare
 * object. The care endpoints answer { status: 'failure', reason: '...' }.
 */
export const hide_note_error_message = (error) =>
  error?.response?.data?.reason || error?.message || 'Could not save. Please try again.';

/**
 * What a click on "Trial Users First 1-on-1 Session only" should do.
 *
 * The checkbox toggles that flag, and on a hidden teacher it also unhides as a side effect — which
 * now needs a reason, so the flag change has to wait for the modal. Waiting means the target value
 * must be CAPTURED at click time: recomputing it after the save reverses the operator's click when
 * the box was already ticked, the state the automatic hide leaves a teacher in (PROD-9278).
 *
 * @param {object} details the teacher being edited
 * @returns {{ needs_note: boolean, trial_only_target: number }}
 */
export const plan_trial_only_click = (details) => ({
  needs_note: details?.hide_from_new_students === 1,
  trial_only_target: details?.trial_1st_1on1_only === 1 ? 0 : 1
});

const HIDE_FLAG_FIELDS = ['hide_from_onboarding', 'hide_from_replacement'];

// PROD-10179: only flags Care changed on this page are sent, so a stale page can't overwrite a system change.
export const changed_hide_flags = (loaded, current) => {
  const as_flag = (value) => (Number(value) === 1 ? 1 : 0);
  const changed = {};
  HIDE_FLAG_FIELDS.forEach((field) => {
    if (as_flag(current?.[field]) !== as_flag(loaded?.[field]))
      changed[field] = as_flag(current?.[field]);
  });
  return changed;
};
